/**
 * External Links API
 * Client-side API for the `externalLinkApi` callable: an admin's management of
 * an external user's personal guest link (Externa användare). Copying the link
 * needs no call — `GuestLinkSummary.url` is the plain-text link.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ExternalLinkTask, GuestLinkKind, GuestLinkLogEntry, GuestLinkSummary } from '../types/guestAccess';
import { recordActivity } from './activityApi';

type ExternalLinkAction = 'get' | 'issue' | 'setValidity' | 'rotate' | 'revoke' | 'send';

interface ApiRequest {
    action: ExternalLinkAction;
    payload: Record<string, unknown>;
}

interface ApiResponse {
    success: true;
    link: GuestLinkSummary | null;
    log?: GuestLinkLogEntry[];
    tasks?: unknown;
    /** rotate and send: whether the e-mail with the link went out */
    emailSent?: boolean;
}

/** Outcome of an action that e-mails the link. */
export interface LinkEmailResult {
    /** False when the link was created but the e-mail failed (the admin can copy or resend). */
    emailSent: boolean;
}

/**
 * The current link (null when the user never had one, or it is revoked), the
 * merged log, newest first, and the tasks the link opens today.
 */
export interface ExternalLinkInfo {
    link: GuestLinkSummary | null;
    log: GuestLinkLogEntry[];
    tasks: ExternalLinkTask[];
}

/** Keep only well-formed task entries (an older backend sends none). */
export function parseLinkTasks(data: unknown): ExternalLinkTask[] {
    if (!Array.isArray(data)) return [];
    return data.flatMap((raw): ExternalLinkTask[] => {
        if (!raw || typeof raw !== 'object') return [];
        const t = raw as Record<string, unknown>;
        if ((t.type !== 'workOrder' && t.type !== 'project') || typeof t.key !== 'string' || typeof t.title !== 'string') return [];
        return [{
            type: t.type,
            key: t.key,
            title: t.title,
            buildingName: typeof t.buildingName === 'string' ? t.buildingName : '',
            ...(typeof t.dueDate === 'string' && t.dueDate ? { dueDate: t.dueDate } : {}),
            ...(typeof t.completedAt === 'string' ? { completedAt: t.completedAt } : {}),
        }];
    });
}

/** Validity of a link: permanent, or expiring on `expiresAt` (absent → 90 days from now). */
export interface LinkValidity {
    kind: GuestLinkKind;
    expiresAt?: string;
}

/**
 * API for personal guest link management (admin only)
 */
class ExternalLinksApi {
    private functions = getFunctions(undefined, 'europe-north1');

    private async call(action: ExternalLinkAction, payload: Record<string, unknown>): Promise<ApiResponse> {
        const externalLinkApi = httpsCallable<ApiRequest, ApiResponse>(this.functions, 'externalLinkApi');
        const result = await externalLinkApi({ action, payload });
        return result.data;
    }

    /** The user's current link, its state and `lastUsedAt`, plus the merged log of all their links. */
    async get(externalUserId: string): Promise<ExternalLinkInfo> {
        const response = await this.call('get', { externalUserId });
        return { link: response.link, log: response.log ?? [], tasks: parseLinkTasks(response.tasks) };
    }

    /** Mint a link for a user without a live one (refused when a non-revoked link exists). */
    async issue(externalUserId: string, validity: LinkValidity): Promise<GuestLinkSummary | null> {
        const response = await this.call('issue', { externalUserId, ...validity });
        recordActivity('externalUser.updated', { targetKey: externalUserId });
        return response.link;
    }

    /** Change an active link's validity; the URL stays the same. Refused for an expired link (create a new one). */
    async setValidity(token: string, validity: LinkValidity): Promise<GuestLinkSummary | null> {
        const response = await this.call('setValidity', { token, ...validity });
        // Never the token as targetKey: it is the access secret.
        recordActivity('externalUser.updated');
        return response.link;
    }

    /**
     * Replace the link and e-mail the new one to the address on file. `validity`
     * sets the new link's; absent, it keeps the old one's kind and period.
     */
    async rotate(token: string, validity?: LinkValidity): Promise<LinkEmailResult> {
        const response = await this.call('rotate', { token, ...validity });
        recordActivity('externalUser.updated');
        return { emailSent: response.emailSent !== false };
    }

    /** Revoke the link with no replacement. */
    async revoke(token: string): Promise<void> {
        await this.call('revoke', { token });
        recordActivity('externalUser.updated');
    }

    /** E-mail the current link to the address on file (an expired one is replaced first). */
    async send(token: string): Promise<LinkEmailResult> {
        const response = await this.call('send', { token });
        recordActivity('externalUser.updated');
        return { emailSent: response.emailSent !== false };
    }
}

export const externalLinksApi = new ExternalLinksApi();
